import React, { useCallback, useRef, useSyncExternalStore } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { useIntl } from 'react-intl';
import { useIssueOverlayControls } from '../../../documentation/DocumentationDataGrid/components/IssueOverlayControls';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box } from '@mui/material';
import { ActionButton } from './actionButton';
import { getCanBeUsedAsMap } from 'components/documentation/model';
import { DocumentationBaseIds } from 'shared/types/documentation';
import { useGalleryContext } from '..';
import { identitySelector } from '../selectors';
import {
  getUserFiltersCount,
  useIssuesGridFilterModel,
} from 'store/issue';
import { FILTER_COUNT_BADGE_SX } from 'components/common/dataGrid/styles';
import { DocumentationTranslationKeys } from 'intl/keys';

interface FilterButtonProps {
  isMobile: boolean;
}

export const FilterButton: React.FC<FilterButtonProps> = ({
  isMobile,
}) => {
  const intl = useIntl();

  const {
    issuesVisibleStore,
    filtersVisibleStore,
    setFiltersVisible,
    filtersDisabled,
    filtersDisabledReason,
  } = useIssueOverlayControls();
  const areIssuesVisible = useSyncExternalStore(
    issuesVisibleStore.subscribe,
    issuesVisibleStore.get
  );
  const isFilterPanelOpen = useSyncExternalStore(
    filtersVisibleStore.subscribe,
    filtersVisibleStore.get
  );
  const wasFilterPanelOpenOnMouseDownRef = useRef(isFilterPanelOpen);
  const { filterModel } = useIssuesGridFilterModel();
  const activeFiltersCount = getUserFiltersCount(filterModel);
  const [file] =
    useGalleryContext<DocumentationBaseIds>().useMainDisplayedFile(
      identitySelector
    );
  const canBeUsedAsMap = getCanBeUsedAsMap(
    file?.data?.extension,
    file?.imageConvertStatus
  );

  const filterAction = formatTranslation(intl, DocumentationTranslationKeys.filterAction,);
  let filtersTooltipTitle = filterAction;
  if (!canBeUsedAsMap) {
    filtersTooltipTitle = formatTranslation(intl, DocumentationTranslationKeys.fileNotSupportedAsMap,);
  } else if (filtersDisabled) {
    filtersTooltipTitle = formatTranslation(intl, unsafeTranslationKey(filtersDisabledReason),);
  }

  const hasActiveUserFilters = activeFiltersCount > 0;
  const canDisplayIssueOverlayFilters =
    canBeUsedAsMap && !filtersDisabled && areIssuesVisible;
  const shouldShowFilterBadge =
    canDisplayIssueOverlayFilters && hasActiveUserFilters;

  const handleMouseDown = useCallback(() => {
    wasFilterPanelOpenOnMouseDownRef.current = filtersVisibleStore.get();
  }, [filtersVisibleStore]);

  const handleClick = useCallback(() => {
    setFiltersVisible(!wasFilterPanelOpenOnMouseDownRef.current);
  }, [setFiltersVisible]);

  return (
    <ActionButton
      isMobile={isMobile}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      disabled={filtersDisabled}
      tooltipTitle={filtersTooltipTitle}
      label={filterAction}
      icon={
        <>
          <TuneRoundedIcon />
          {shouldShowFilterBadge ? (
            <Box sx={FILTER_COUNT_BADGE_SX}>
              {activeFiltersCount}
            </Box>
          ) : null}
        </>
      }
    />
  );
};
